import meta from '@/Components/Planner/plannerVideoMeta.json'
import { takePlannerHelpFirstVisit } from '@/composables/plannerHelp'
import { replaceQueryParam, useQueryParam } from '@/composables/route'
import { usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'

/**
 * „So funktioniert's“ (Arnold, 02.11.2026, visual plan TK8PVZg3BL3AjnduVBnFsw): one modal for the
 * whole page (mounted once in Layouts/Default.vue) that the navigation row, the pill below lg, the
 * start page and ?video=1 all open. The files live in a bucket outside the repository
 * (`planner.video.base_url`, shared as `_planner.videoBaseUrl`); without an address nothing offers
 * the video (LM2). plannerVideoMeta.json names the recorded version and its chapters (written by
 * tools/planner-video/record.mjs).
 */

export const PLANNER_VIDEO_HELP_ID = 'how_it_works'

export const plannerVideoOpen = ref(false)

/** UX2: the first-visit dot, taken once per page load and gone once the video was opened. */
const dot = ref(null)

export const plannerVideoMeta = meta

/**
 * UX3: the German recording for German users, the English one for every other locale.
 *
 * @param {string} locale e.g. `de-DE` (document.documentElement.lang)
 * @returns {'de'|'en'}
 */
export const plannerVideoVersion = (locale) =>
  locale === 'de-DE' ? 'de' : 'en'

/**
 * @param {number} seconds
 * @returns {string} e.g. `1:28`
 */
export const formatVideoDuration = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

export const plannerVideoDuration = formatVideoDuration(meta.durationSeconds)

/** The version the current page language plays. */
export const currentPlannerVideo = () =>
  meta.versions[plannerVideoVersion(document.documentElement.lang)]

/** Whether the video is offered: true when the planner shares a non-empty `videoBaseUrl`. */
export const usePlannerVideoAvailable = () =>
  computed(() => {
    const base = usePage().props._planner?.videoBaseUrl

    return typeof base === 'string' && base !== ''
  })

export const plannerVideoSource = () => {
  const base = usePage().props._planner?.videoBaseUrl ?? null

  return base === null ? null : `${base}/${currentPlannerVideo().file}`
}

/** Whether the dot shows; asks the browser once per page load (the row and the pill share it). */
export const usePlannerVideoDot = () => {
  if (dot.value === null) {
    dot.value = takePlannerHelpFirstVisit(
      usePage().props._user?.id ?? null,
      PLANNER_VIDEO_HELP_ID,
    )
  }

  return computed(() => dot.value === true)
}

export const openPlannerVideo = () => {
  plannerVideoOpen.value = true
  dot.value = false
}

export const closePlannerVideo = () => {
  plannerVideoOpen.value = false
}

/** CS1: `?video=1` opens the video, then leaves the address without the flag. */
export const openPlannerVideoFromUrl = () => {
  if (useQueryParam().video !== '1') {
    return
  }

  replaceQueryParam('video', null)
  openPlannerVideo()
}

/** Specs only: a fresh page load. */
export const resetPlannerVideoForTests = () => {
  plannerVideoOpen.value = false
  dot.value = null
}
